import React from 'react';

interface UnifiedLoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  color?: 'primary' | 'secondary' | 'white' | 'gray' | 'green' | 'blue' | 'amber';
  variant?: 'spinner' | 'dots' | 'pulse' | 'ring';
  className?: string;
  message?: string;
  fullScreen?: boolean;
  backdrop?: boolean;
}

const UnifiedLoadingSpinner: React.FC<UnifiedLoadingSpinnerProps> = ({
  size = 'md',
  color = 'green',
  variant = 'spinner',
  className = '',
  message,
  fullScreen = false,
  backdrop = true,
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'xs':
        return 'w-3 h-3';
      case 'sm':
        return 'w-4 h-4';
      case 'md':
        return 'w-6 h-6';
      case 'lg':
        return 'w-8 h-8';
      case 'xl':
        return 'w-12 h-12';
      case '2xl':
        return 'w-16 h-16';
      default:
        return 'w-6 h-6';
    }
  };

  const getColorClasses = () => {
    switch (color) {
      case 'primary':
        return 'text-blue-600';
      case 'secondary':
        return 'text-gray-600';
      case 'white':
        return 'text-white';
      case 'gray':
        return 'text-gray-400';
      case 'green':
        return 'text-green-600';
      case 'blue':
        return 'text-blue-500';
      case 'amber':
        return 'text-amber-600';
      default:
        return 'text-green-600';
    }
  };

  const renderSpinner = () => {
    switch (variant) {
      case 'dots':
        return (
          <div className={`flex space-x-1 ${getSizeClasses()}`}>
            <div
              className={`w-2 h-2 bg-current rounded-full animate-bounce ${getColorClasses()}`}
              style={{ animationDelay: '0ms' }}
            />
            <div
              className={`w-2 h-2 bg-current rounded-full animate-bounce ${getColorClasses()}`}
              style={{ animationDelay: '150ms' }}
            />
            <div
              className={`w-2 h-2 bg-current rounded-full animate-bounce ${getColorClasses()}`}
              style={{ animationDelay: '300ms' }}
            />
          </div>
        );

      case 'pulse':
        return (
          <div className={`${getSizeClasses()} ${getColorClasses()}`}>
            <div className="w-full h-full bg-current rounded-full animate-pulse" />
          </div>
        );

      case 'ring':
        return (
          <div className={`${getSizeClasses()} ${getColorClasses()}`}>
            <div
              className={`w-full h-full border-2 border-current border-t-transparent rounded-full animate-spin`}
            />
          </div>
        );

      case 'spinner':
      default:
        return (
          <svg
            className={`animate-spin ${getSizeClasses()} ${getColorClasses()}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        );
    }
  };

  if (fullScreen) {
    return (
      <div
        className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${
          backdrop ? 'bg-white bg-opacity-75' : 'bg-transparent'
        }`}
        data-testid="fullscreen-loader"
      >
        {renderSpinner()}
        {message && (
          <p
            className="text-gray-700 text-center text-lg font-medium mt-4"
            data-testid="loader-message"
          >
            {message}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className={`inline-flex flex-col items-center ${className}`}
      data-testid="unified-loading-spinner"
    >
      {renderSpinner()}
      {message && (
        <p className="text-gray-600 text-sm mt-2 text-center" data-testid="loader-message">
          {message}
        </p>
      )}
    </div>
  );
};

export default UnifiedLoadingSpinner;
